import { useState, useEffect, useRef, useCallback } from 'react'
import { useRouter } from 'next/router'
import { useSelector } from 'react-redux'

import Box from '@mui/material/Box'
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import { Navigation, Pagination, Autoplay, Lazy } from 'swiper'
import { SwiperSlide } from 'swiper/react'
import 'swiper/css'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'

import DesktopPaginationWithTitle from 'components/common/Banner/DesktopPaginationWithTitle'
import BannerWrapper from 'components/common/Banner/BannerWrapper'
import { StyledSwiper } from 'components/common/Banner/StyledSwiper'
import { BannerImg } from 'components/common/Banner'
import { useBanners } from 'utils/api/hooks'
import { sendBannerClick } from 'utils/hooks/analytics'
import { genPathByNavData } from 'utils/api'
import { selectWebview } from 'store/webview/webviewSlice'
import { useBannerGAForCarousel } from 'utils/hooks/useBannerGA'

const modules = [Navigation, Pagination, Autoplay, Lazy]

const Banner = ({ banner, router, isMobile, isFirst, onBannerClick }) => {
  if (isMobile) {
    return isFirst ? (
      <BannerImg src={banner?.imageDouble?.url} onClick={onBannerClick} />
    ) : (
      <BannerImg
        data-src={banner?.imageDouble?.url}
        onClick={onBannerClick}
        className="swiper-lazy"
      />
    )
  }

  return <BannerImg src={banner?.image?.url} onClick={onBannerClick} />
}

const swiperSettings = {
  spaceBetween: 24,
  slidesPerView: 'auto',
  centeredSlides: true,
  loop: true,
  pagination: { el: '.banner-pagination', clickable: true },
  slideToClickedSlide: true,
}

const swiperAutoplay = {
  delay: 5000,
  disableOnInteraction: false,
}

const BannerCarousel = ({ fallbackData, isMobile }) => {
  const webview = useSelector(selectWebview)
  const [swiper, setSwiper] = useState(null)
  const [swiperIndex, setSwiperIndex] = useState(0)
  const [hovered, setHovered] = useState(false)
  const onNextClick = () => swiper?.slideNext()
  const onPrevClick = () => swiper?.slidePrev()
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'))

  const { data } = useBanners(
    webview?.name,
    null,
    fallbackData ? { fallbackData } : undefined
  )
  const banners = fallbackData || data
  const router = useRouter()

  const onBannerClick = (banner) => async () => {
    sendBannerClick({
      banner,
      bannerLocation: '首頁',
    })

    const { path, isExternalLink } = await genPathByNavData(banner.navData)
    if (path) {
      if (isExternalLink) {
        window.open(path, '_blank')
      } else {
        router.push(path)
      }
    }
  }

  useBannerGAForCarousel({
    bannerData: banners,
    bannerLocation: '首頁',
    swiper,
    isRepeated: false,
  })

  useBannerGAForCarousel({
    bannerData: banners,
    bannerLocation: '首頁',
    swiper,
    isRepeated: true,
  })

  const onIndexChange = (swiper) => {
    if (swiper.realIndex > -1) {
      setSwiperIndex(swiper.realIndex)
    }
  }

  // 設0時會有bug(無法顯示左圖)，當0時index設定為length但跳轉第二張圖有反向行為，因此設定指定位址為下一個loop的index
  const onPaginationIndexClick = (index) =>
    swiper?.slideTo(banners.length + index)

  if (!banners?.length) {
    return null
  }

  const swiperNavigation = {
    nextEl: `.instance-${banners[0]?._id} .banner-swiper-next`,
    prevEl: `.instance-${banners[0]?._id} .banner-swiper-prev`,
  }

  // 防止 banner 在 mobile 輪播時圖片還沒載入完成
  const swiperLazyOptions = isMobile
    ? {
        loadOnTransitionStart: true,
        loadPrevNext: true,
      }
    : false

  return (
    <BannerWrapper mb={isDesktop ? 10 : 6}>
      <Box
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <StyledSwiper
          modules={modules}
          {...swiperSettings}
          preloadImages={!isMobile}
          lazy={swiperLazyOptions}
          watchSlidesProgress={isMobile}
          loopedSlides={banners.length}
          navigation={banners.length > 1 ? swiperNavigation : false}
          autoplay={banners.length > 1 ? swiperAutoplay : false}
          onAfterInit={(swiper) => setSwiper(swiper)}
          onRealIndexChange={onIndexChange}
        >
          {banners.length > 1 && (
            <>
              {hovered && (
                <Box className={`instance-${banners[0]?._id}`}>
                  <div className="banner-swiper-next" onClick={onNextClick}>
                    <ArrowForwardIosIcon fontSize="small" />
                  </div>
                  <div className="banner-swiper-prev" onClick={onPrevClick}>
                    <ArrowBackIosIcon fontSize="small" />
                  </div>
                </Box>
              )}
              <div className="banner-pagination" />
            </>
          )}

          {banners?.map((banner, index) => (
            <SwiperSlide key={banner._id}>
              <Banner
                banner={banner}
                router={router}
                isMobile={isMobile}
                isFirst={index === 0}
                onBannerClick={onBannerClick(banner)}
              />
            </SwiperSlide>
          ))}

          {isDesktop && (
            <DesktopPaginationWithTitle
              data={banners}
              currentIndex={swiperIndex}
              onPaginationIndexClick={onPaginationIndexClick}
            />
          )}
        </StyledSwiper>
      </Box>
    </BannerWrapper>
  )
}

export default BannerCarousel
